'use client'

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';

// 1. Context için tür tanımlaması
interface ExtraToolContextType {
  isCalculatorOpen: boolean;
  openCalculator: () => void;
  closeCalculator: () => void;
  isStopWatchOpen: boolean;
  openStopWatch: () => void;
  closeStopWatch: () => void;
  isMaskingPageOpen: boolean;
  openMaskingPage: () => void;
  closeMaskingPage: () => void;
  isSavedPageOpen: boolean;
  openSavedPage: () => void;
  closeSavedPage: () => void;
  toggleSavedPage: () => void;
  isNumeratorOpen: boolean;
  openNumerator: () => void;
  closeNumerator: () => void;
  toggleNumerator: () => void;
}

// 2. Varsayılan değer (boş obje, ancak uygun tür için `as` kullanılır)
const ExtraToolContext = createContext<ExtraToolContextType | undefined>(
  undefined,
);

// 3. Provider Component
interface ExtraToolProviderProps {
  children: ReactNode; // children için tür
}

export const ExtraToolProvider: React.FC<ExtraToolProviderProps> = ({
  children,
}) => {
  const [isCalculatorOpen, setIsCalculatorOpen] = useState<boolean>(false);
  const [isStopWatchOpen, setIsOpenStopWatch] = useState<boolean>(false);
  const [isMaskingPageOpen, setIsMaskingPageOpen] = useState<boolean>(false);
  const [isSavedPageOpen, setIsSavedPageOpen] = useState<boolean>(false);
  const [isNumeratorOpen, setIsNumeratorOpen] = useState<boolean>(false);

  const openCalculator = useCallback(() => setIsCalculatorOpen(true), []);
  const closeCalculator = useCallback(() => setIsCalculatorOpen(false), []);

  const openStopWatch = useCallback(() => setIsOpenStopWatch(true), []);
  const closeStopWatch = useCallback(() => setIsOpenStopWatch(false), []);

  const openMaskingPage = useCallback(() => setIsMaskingPageOpen(true), []);
  const closeMaskingPage = useCallback(() => setIsMaskingPageOpen(false), []);

  const openSavedPage = useCallback(() => {
    setIsSavedPageOpen(true);
  }, []);

  const closeSavedPage = useCallback(() => {
    setIsSavedPageOpen(false);
  }, []);

  const toggleSavedPage = useCallback(() => {
    setIsSavedPageOpen(!isSavedPageOpen);
  }, [isSavedPageOpen]);

  const openNumerator = useCallback(() => setIsNumeratorOpen(true), []);
  const closeNumerator = useCallback(() => setIsNumeratorOpen(false), []);
  const toggleNumerator = useCallback(
    () => setIsNumeratorOpen(!isNumeratorOpen),
    [isNumeratorOpen],
  );

  return (
    <ExtraToolContext.Provider
      value={{
        isCalculatorOpen,
        openCalculator,
        closeCalculator,
        isStopWatchOpen,
        openStopWatch,
        closeStopWatch,
        isMaskingPageOpen,
        openMaskingPage,
        closeMaskingPage,
        isSavedPageOpen,
        openSavedPage,
        closeSavedPage,
        toggleSavedPage,
        isNumeratorOpen,
        openNumerator,
        closeNumerator,
        toggleNumerator
      }}
    >
      {children}
    </ExtraToolContext.Provider>
  );
};

export const useExtraTool = () => {
  const context = useContext(ExtraToolContext);
  if (!context)
    throw new Error('useSidebar must be used within a SidebarProvider');
  return context;
};
